import '../Sass/App.scss';
import React, { useContext, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { CartContext } from '../Contexts/CartContext';
import { apiFetch } from '../Helpers/api';

function formatPrice(price) {
  return `$${Number(price || 0).toFixed(2)}`;
}

function creditText(item) {
  if (!item.credit_applied) {
    return '';
  }

  return `${item.credited_quantity} codebase credit${item.credited_quantity === 1 ? '' : 's'} applied`;
}

export function ShoppingCart(props) {
  const cart = useContext(CartContext);
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutWorking, setCheckoutWorking] = useState(false);

  async function checkout() {
    if (cart.itemCount === 0) {
      return;
    }

    setCheckoutMessage('');
    setCheckoutError('');

    if (cart.unappliedCodebaseCredits > 0) {
      setCheckoutError(`checkout blocked: add ${cart.unappliedCodebaseCredits} more codebase${cart.unappliedCodebaseCredits === 1 ? '' : 's'} to use included bootkit credits`);
      return;
    }

    setCheckoutWorking(true);
    setCheckoutMessage('initializing stripe checkout...');

    try {
      const response = await apiFetch('createcheckout.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.rawItems.map((item) => ({
            product_type: item.product_type,
            product_id: item.product_id,
            quantity: item.quantity,
          })),
        }),
      });
      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Unable to start checkout.');
      }

      if (!json.data?.checkout_url) {
        throw new Error('Checkout did not return a Stripe URL.');
      }

      window.location.assign(json.data.checkout_url);
    } catch (error) {
      setCheckoutMessage('');
      setCheckoutError(error.message);

      if (error.message.toLowerCase().includes('logged in') && props.openProfile) {
        props.openProfile('login');
      }
    } finally {
      setCheckoutWorking(false);
    }
  }

  function closeCart() {
    setCheckoutMessage('');
    setCheckoutError('');
    props.onClose();
  }

  return (
    <Dialog
      open={props.open}
      maxWidth="sm"
      fullWidth={true}
      onClose={closeCart}
      PaperProps={{ sx: { bgcolor: 'black', border: 1, borderColor: '#00B000', padding: 3, color: 'white' } }}
    >
      <div className="shopping-cart">
        <div className="cart-header">
          <h2>shopping cart</h2>
          <button className="close-button cart-close-button" onClick={closeCart}>x</button>
        </div>
        <div className="cart-command-line">
          <span className="green-code">cart&gt;</span>
          <span className="white-code">list --products</span>
        </div>
        {cart.itemCount === 0 ? (
          <div className="cart-empty">
            <div className="grey-code">no products selected</div>
          </div>
        ) : (
          <div className="cart-items">
            {cart.items.map((item, index) => (
              <div className="cart-item" key={item.cart_key || item.codebase_id}>
                <div className="cart-item-index green-code">{String(index + 1).padStart(2, '0')}</div>
                <div className="cart-item-info">
                  <div className="cart-item-name">{item.name}<span className="component-tag">{item.product_type || 'codebase'}</span></div>
                  
                  {item.designer && <div className="grey-code">by {item.designer}</div>}
                  {item.description && <div className="cart-item-description">{item.description}</div>}
                  {item.credit_applied && <div className="cart-credit-note">{creditText(item)}</div>}
                  <div className="cart-quantity">
                    <span className="grey-code">qty</span>
                    <button className="cli-button cart-quantity-button" onClick={() => cart.decreaseItem(item.cart_key)}>-</button>
                    <input
                      aria-label={`${item.name} quantity`}
                      value={item.quantity}
                      onChange={(event) => cart.setItemQuantity(item.cart_key, event.target.value)}
                    />
                    <button className="cli-button cart-quantity-button" onClick={() => cart.increaseItem(item.cart_key)}>+</button>
                  </div>
                </div>
                <div className="cart-item-price">
                  {item.credit_applied && <div className="cart-original-price">{formatPrice(item.line_original_total)}</div>}
                  <div>{formatPrice(item.line_final_total ?? item.price)}</div>
                </div>
                <button className="cli-button-red" onClick={() => cart.removeItem(item.cart_key || 'codebase:' + item.codebase_id)}>remove</button>
              </div>
            ))}
          </div>
        )}
        <div className="cart-summary">
          <div>
            <div className="grey-code">items</div>
            <div className="white-code">{cart.itemCount}</div>
          </div>
          <div>
            <div className="grey-code">total</div>
            <div className="green-code">{formatPrice(cart.total)}</div>
          </div>
          <div>
            <div className="grey-code">codebase credits</div>
            <div className="white-code">{cart.codebaseCount}/{cart.codebaseCreditCount}</div>
          </div>
          <div>
            <div className="grey-code">before credits</div>
            <div className="green-code">{formatPrice(cart.subtotal)}</div>
          </div>
        </div>
        {cart.unappliedCodebaseCredits > 0 && (
          <div className="cart-warning">
            add {cart.unappliedCodebaseCredits} more codebase{cart.unappliedCodebaseCredits === 1 ? '' : 's'} before checkout
          </div>
        )}
        {checkoutError && <div className="cart-warning">{checkoutError}</div>}
        {checkoutMessage && <div className="cart-checkout-message">{checkoutMessage}</div>}
        <div className="cart-actions">
          <button className="cli-button" onClick={checkout} disabled={cart.itemCount === 0 || checkoutWorking}>
            {checkoutWorking ? 'starting checkout...' : 'checkout'}
          </button>
          <button className="cli-button-white" onClick={cart.clearCart} disabled={cart.itemCount === 0}>clear cart</button>
        </div>
      </div>
    </Dialog>
  );
}
